<template>
  <StructuredData id="schema-webpage" :schema="webPageSchema" />
  <div v-if="agrupacion" class="overflow-hidden">
    <div
      class="landing-experiencia info-viaje-grupo"
      :class="{ 'with-index-block': hasIndexBlockDesignA }"
      :style="landingStyle">
      <template v-for="block in sortedBlocks" :key="block.id">
        <div
          :id="`block-${block.id}`"
          class="block-wrapper"
          :class="[
            block.tipo !== BLOCK_TYPES.INDEX ? getBlockSpacingClasses(block, agrupacion.style?.spacing) : null,
            block.tipo !== BLOCK_TYPES.INDEX ? 'block-reveal' : null,
          ]"
          :style="getSectionWrapperStyle(block, agrupacion.style)">
          <div class="landing-content">
            <component
              :is="getBlockComponent(block.tipo, dispositivo, ENTITY_TYPES.AGRUPACION)"
              :agrupacion="agrupacion"
              :block="block" />
          </div>
        </div>
      </template>
    </div>

    <GruposListaEsperaModal
      v-if="showListaEsperaModal"
      :viaje-id="agrupacion.id"
      :style-vars="landingStyle"
      @close="showListaEsperaModal = false" />
  </div>
</template>

<script setup>
  import { ref, computed, onUnmounted, onBeforeMount, nextTick } from 'vue';
  import { useRoute } from 'vue-router';
  import { useAgrupacionesStore } from '@/store_pinia/agrupaciones';
  import GruposListaEsperaModal from '@/components/modales/GruposListaEsperaModal.vue';
  import { saveComercialId } from '@/helpers/viajesGrupos';
  import AgrupacionesApiService from '@/services/api/AgrupacionesApiService';
  import { getBlockComponent, sortBlocksByOrder } from '@/helpers/builderBlocksUtils';
  import { DESIGN_A, DISPOSITIVO, ENTITY_TYPES, BLOCK_TYPES } from '@/helpers/builderBlocksUtils';
  import {
    landingStyleVars,
    loadLandingFonts,
    getBlockSpacingClasses,
    getSectionWrapperStyle,
  } from '@/helpers/landingStyleUtils';
  import { agenciaStoreModule } from '@/store_pinia/agencia';
  import metaPixelUtils from '@/services/utils/metaPixelUtils';
  import gtagPixelUtils from '@/services/utils/gtagPixelUtils';
  import { useLoadingStore } from '@/store_pinia/loadingStore';
  import StructuredData from '@/components/utils/StructuredData.vue';
  import { usePageMeta } from '@/composables/usePageMeta';
  import { setupLandingScrollReveal } from '@/helpers/scrollAnimationUtils';

  const agrupacionesStore = useAgrupacionesStore();
  const agenciaStore = agenciaStoreModule();
  const loadingStore = useLoadingStore();

  const route = useRoute();

  const agrupacion = ref(null);
  const { webPageSchema } = usePageMeta({
    title: computed(() => agrupacion.value?.nombre),
    image: computed(() => agrupacion.value?.imagen),
  });
  const showListaEsperaModal = ref(false);
  const builderBlocks = ref([]);
  const stopReveal = ref(null);
  const uri = route.params.uri;
  const codigoAgrupacion = route.query.codigo;
  const codigoPrevisualizacion = route.query.codigoPrevisualizacion;

  const dispositivo = computed(() => {
    return window.innerWidth < 768 ? DISPOSITIVO.MOBILE : DISPOSITIVO.DESKTOP;
  });

  const sortedBlocks = computed(() => {
    return sortBlocksByOrder(builderBlocks.value);
  });

  const landingStyle = computed(() => landingStyleVars(agrupacion.value?.style));

  const hasIndexBlockDesignA = computed(() => {
    return builderBlocks.value.some(
      block => block.tipo === BLOCK_TYPES.INDEX && block.design === DESIGN_A && block.visible === true
    );
  });

  function handleResize() {
    dispositivo.value = window.innerWidth < 768 ? DISPOSITIVO.MOBILE : DISPOSITIVO.DESKTOP;
  }

  async function loadAgrupacion() {
    try {
      loadingStore.show();
      agrupacion.value = await agrupacionesStore.fetchAgrupacion(uri, codigoAgrupacion, codigoPrevisualizacion);
      loadLandingFonts(agrupacion.value?.style);
      sendViewContentEvent();
    } catch (error) {
      console.error('Error al cargar la agrupación:', error);
    } finally {
      loadingStore.hide();
    }
  }

  async function loadBuilderBlocks() {
    try {
      loadingStore.show();
      const response = await AgrupacionesApiService.getAgrupacionBuilderModule(uri, codigoAgrupacion, codigoPrevisualizacion);
      builderBlocks.value = Array.isArray(response) ? response : response?.modules ?? [];
    } catch (error) {
      console.error('Error al cargar los bloques del builder:', error);
    } finally {
      loadingStore.hide();
    }
  }

  function sendViewContentEvent() {
    if (agrupacion.value && agrupacion.value.id) {
      metaPixelUtils.sendViewContentEvent(
        agrupacion.value.id,
        agenciaStore.getCurrencyName,
        agrupacion.value.precioDesde || 0
      );
      gtagPixelUtils.sendViewItemEvent(
        agrupacion.value.id,
        agenciaStore.getCurrencyName,
        agrupacion.value.precioDesde || 0
      );
    }
  }

  async function init() {
    try {
      loadingStore.show();
      await saveComercialId(route.query.comercialId);
      await loadAgrupacion();
      await loadBuilderBlocks();
      await nextTick();
      stopReveal.value = await setupLandingScrollReveal();
    } catch (error) {
      console.error(error);
    } finally {
      loadingStore.hide();
    }
  }

  onBeforeMount(async () => {
    await init();
    window.addEventListener('resize', handleResize);
  });

  onUnmounted(() => {
    window.removeEventListener('resize', handleResize);
    stopReveal.value?.();
  });
</script>

<style scoped lang="scss">
  .loading {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 400px;
  }

  .block-wrapper {
    scroll-margin-top: 180px;

    @media (max-width: 768px) {
      scroll-margin-top: 100px;
    }
  }

  .info-viaje-grupo {
    margin: 0 auto;
    margin-top: 100px;
    position: relative;

    &.with-index-block {
      margin-top: 167px;

      @media (max-width: 768px) {
        margin-top: 152px;
      }
    }

    @media (max-width: 768px) {
      padding: 0;
      margin-top: 78px;
    }
  }
</style>
